import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { paths } from "../../App";
import { ReactComponent as ChevronRight } from "../../assets/icons/chevron_right.svg";
import { ReactComponent as IconGlobe } from "../../assets/icons/globe.svg";
import LocationInfo from "../../components/LocationInfo";
import OrganizationImage from "../../components/OrganizationImage";
import prettyUrl from "../../helpers/prettyUrl";
import { EventInfo, Organization } from "../../types";
import * as styles from "./styles";
import PracticalInformationPreview from "../../components/PracticalInformationModal/practicalInformationPreview";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevron_down.svg";
import {
  Description as DescComponent,
  EventInfoBox,
  DescriptionSkeleton as DescSkeleton,
  EventInfoBoxSkeleton,
  EventMapSkeleton,
} from "@hooplab/ui-components";

type DescriptionProps = {
  text?: string;
  maxHeight?: number;
  fontSize?: string;
  hideHeader?: boolean;
  organization?: Organization;
  event?: EventInfo;
  setPracticalInfoModalOpen: () => void;
};

const Description: React.FC<DescriptionProps> = (props) => {
  const intl = useIntl();
  return (
    <>
      <DescComponent
        text={props.text}
        maxHeight={props.maxHeight}
        fontSize={props.fontSize}
        showLessContent={
          <div
            className={styles.expandButton}
            aria-label={intl.formatMessage({
              id: "screens.Event.description.showLessButton",
              defaultMessage: "Show less of description",
            })}
          >
            <ChevronDown height={18} width={18} className="rotate-180" />
          </div>
        }
        showMoreContent={
          <div
            className={styles.expandButton}
            aria-label={intl.formatMessage({
              id: "screens.Event.description.showMoreButton",
              defaultMessage: "Show more of description",
            })}
          >
            <ChevronDown height={18} width={18} />
          </div>
        }
      />
      {props.event && props.event.information.length > 0 && (
        <PracticalInformationPreview
          info={props.event.information}
          onSetModalOpen={props.setPracticalInfoModalOpen}
        />
      )}
      <div className={styles.organizerInfo}>
        {props.organization && (
          <EventInfoBox
            illustration={
              <div className="mr-2">
                <OrganizationImage organization={props.organization} />
              </div>
            }
            title={
              <span className={styles.organizerInfoBoxHeader}>
                <FormattedMessage
                  id={"screens.Event.Description.hostedBy"}
                  defaultMessage="Hosted by"
                />
              </span>
            }
            information={
              <div className="truncate">
                <span style={{ fontWeight: 500 }}>{props.organization.name}</span>
              </div>
            }
          />
        )}
        {props.organization && props.organization.website && (
          <EventInfoBox
            illustration={
              <div className={styles.globe}>
                <IconGlobe height={22} />
              </div>
            }
            title={
              <span className={styles.organizerInfoBoxHeader}>
                <FormattedMessage
                  id={"screens.Event.Description.website"}
                  defaultMessage="Website"
                />
              </span>
            }
            information={
              <a
                href={"https://" + prettyUrl(props.organization.website)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: 500 }}
                className="truncate"
              >
                {prettyUrl(props.organization.website)}
              </a>
            }
          />
        )}
      </div>
      {props.event && <LocationInfo event={props.event} />}
      {props.organization && (
        <div style={{ marginTop: "1rem" }}>
          <Link to={paths.organization()}>
            <EventInfoBox
              title={
                <span className={styles.organizerInfoBoxHeader}>
                  <FormattedMessage
                    id={"screens.Event.Description.organizer"}
                    defaultMessage="Organizer"
                  />
                </span>
              }
              information={
                <span style={{ fontWeight: 500 }}>
                  <FormattedMessage
                    id={"screens.Event.eventsAtOrganizer"}
                    defaultMessage={"Events at {organizer}"}
                    values={{ organizer: props.organization.name }}
                  />
                </span>
              }
              endButton={
                <div className={styles.chevron}>
                  <ChevronRight height={22} color={"#151515"} />
                </div>
              }
            />
          </Link>
        </div>
      )}
    </>
  );
};

export const DescriptionSkeleton: React.FC<{ maxHeight?: number }> = ({ maxHeight }) => {
  return (
    <>
      <DescSkeleton maxHeight={maxHeight} />
      <div className={styles.organizerInfo}>
        <EventInfoBoxSkeleton title={<></>} information={<></>} illustration={<></>} />
        <EventInfoBoxSkeleton title={<></>} information={<></>} illustration={<></>} />
      </div>
      <EventMapSkeleton />
      <div style={{ marginTop: "1rem" }}>
        <EventInfoBoxSkeleton title={<></>} information={<></>} endButton={<></>} />
      </div>
    </>
  );
};

export default Description;
